import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import type { Pass } from "three/examples/jsm/postprocessing/Pass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { CLOUD_GLSL, CLOUD_SOFTNESS, NO_CLOUD_SHADOW, setCloudShadow, type CloudShadow, type CloudShadowUniforms } from "./cloud-shadow";
import { GRADE_GLSL, NO_GRADE, setGrade, type ColourGrade, type GradeUniforms } from "./grade";
import { defaultSway, ensureSway, patchSwayShader, pushUniforms, swayDepthMaterial, swayKey, sways, type PushUniforms } from "./foliage";

// The low-poly look (decision 0034), beside the toon look (0013): flat shading with vertex colours from a palette
// table per scene, one shared surface material (so a whole scene compiles one lit program), a `glow` vertex attribute
// for emissive faces (eyes, glyphs, windows) that skips the lighting, a warm low sun with a sky and ground fill, a
// gradient sky dome, distance and height haze that fades into the sky's horizon colour, tone mapping and anti-aliasing,
// bloom on the tiers that ask for it, blob shadows under characters on the tiers that ask for them, and one sun
// shadow map sized per tier that follows a focus point. The game owns every value: the scene look (palette, sky,
// haze, sun, fill, exposure) and the tier table. The surface also bends geometry with a `sway` weight away from the
// game's pushers (foliage.ts, decision 0085), in its own pass and in the shadow pass (its depth twin), and takes the
// key light away under drifting cloud shadow (cloud-shadow.ts, decision 0088) when the look sets one, and grades its
// own colour (saturation and a tint, grade.ts, IMP-0024) when the look sets a grade.

/** One scene's look: the palette its content colours come from, and its light and air. */
export interface LowPolySceneLook {
  /** Named colours ("grass.light": "#7cb85a"); content refers to them by key. */
  palette: Record<string, string>;
  /** The sky dome's gradient: overhead, at the horizon, below it. The horizon is also the haze colour. */
  sky: { top: string; horizon: string; ground: string };
  /** Distance and height haze: none before `near` (m), then 1 − e^(−density·d), thinned above `base` (m) by
   *  e^(−falloff·h) (`height` 0 to 1 weights that), capped at `max`. */
  haze: { near: number; density: number; base: number; falloff: number; height: number; max: number };
  /** The sun: colour, intensity, elevation and azimuth (radians; azimuth 0 is +z, increasing toward +x). */
  sun: { colour: string; intensity: number; elevation: number; azimuth: number };
  /** The sky and ground fill (a hemisphere light). */
  fill: { sky: string; ground: string; intensity: number };
  /** Tone-mapping exposure (ACES filmic). */
  exposure: number;
  /** Glowing faces' brightness (1: their colour as is). */
  glow: number;
  /** How lit the night lamps are, 0 to 1 (faces with glow 2: windows, lanterns): 0 lit like any face, 1 glowing.
   *  Default 0 (time of day sets it; phase C). */
  lamps?: number;
  /** How much wet ground darkens, 0 to 1 (upward faces darken by this; time of day and weather set it). Default 0. */
  wet?: number;
  /** Cloud shadow lying still on the scene (`offset`, m, moves the field; default none). A time-of-day driver writes
   *  a drifting one each frame instead (`DayKey.cloudShadow`, `applyDayLook`). */
  cloudShadow?: CloudShadow & { offset?: [number, number] };
  /** A colour grade on the surface (saturation, tint; default none). A time-of-day driver writes the hour's instead
   *  (`DayKey.grade`, `applyDayLook`). */
  grade?: ColourGrade;
}

/** One quality tier of the low-poly look. */
export interface LowPolyTier {
  /** Device pixel ratio cap. */
  pixelRatioCap: number;
  /** Multisampled anti-aliasing (the canvas's, read at renderer creation; and the post chain's target). */
  msaa: boolean;
  /** The sun's shadow map, square (0: no shadow map). */
  shadowMapSize: number;
  /** "all": every mesh casts; "characters": only roots tagged `userData.character`. */
  shadowCasters: "all" | "characters";
  /** A soft blob under each tracked character. */
  blobShadows: boolean;
  /** Bloom (a post chain: render, bloom, output) or none (the scene draws straight to the canvas). */
  bloom: { strength: number; radius: number; threshold: number } | null;
}

/** The attribute glowing faces carry (meshkit's `facet` writes it): 0 lit, 1 unlit at `glow` brightness, 2 a night
 *  lamp (lit by day, glowing as the look's `lamps` rises). */
export const LOWPOLY_GLOW_ATTRIBUTE = "glow";

/** A palette lookup: a key from the scene's palette, or a hex colour. Unknown keys throw (a content bug). */
export function createPalette(palette: Record<string, string>): (c: string) => THREE.Color {
  const cache = new Map<string, THREE.Color>();
  return (c) => {
    let v = cache.get(c);
    if (v) return v;
    const hex = c.startsWith("#") ? c : palette[c];
    if (!hex) throw new Error(`@rpg/look: no palette colour "${c}"`);
    v = new THREE.Color(hex);
    cache.set(c, v);
    return v;
  };
}

/** The surface material's shared uniforms (one set per scene look; the foliage push's and the cloud shadow's among
 *  them). */
export interface LowPolyUniforms extends PushUniforms, CloudShadowUniforms, GradeUniforms {
  uLpGlow: THREE.IUniform<number>;
  uLpHazeColor: THREE.IUniform<THREE.Color>;
  uLpHaze: THREE.IUniform<THREE.Vector4>;
  uLpHaze2: THREE.IUniform<THREE.Vector2>;
  /** The night lamps' glow, 0 to 1. */
  uLpLamp: THREE.IUniform<number>;
  /** Wet ground's darkening, 0 to 1. */
  uLpWet: THREE.IUniform<number>;
}

/** The uniforms for a scene look (shared by the surface and the sky). */
export function lowPolyUniforms(look: LowPolySceneLook): LowPolyUniforms {
  const u: LowPolyUniforms = {
    uLpGlow: { value: 1 },
    uLpHazeColor: { value: new THREE.Color() },
    uLpHaze: { value: new THREE.Vector4() },
    uLpHaze2: { value: new THREE.Vector2() },
    uLpLamp: { value: 0 },
    uLpWet: { value: 0 },
    uLpCloud: { value: new THREE.Vector4() },
    uLpCloudAt: { value: new THREE.Vector2() },
    uLpGrade: { value: new THREE.Vector4(1, 1, 1, 1) },
    ...pushUniforms(),
  };
  setLowPolyUniforms(u, look);
  return u;
}

/** Write a scene look into its uniforms (a time-of-day change, say). */
export function setLowPolyUniforms(u: LowPolyUniforms, look: LowPolySceneLook): void {
  u.uLpGlow.value = look.glow;
  u.uLpHazeColor.value.set(look.sky.horizon);
  u.uLpHaze.value.set(look.haze.near, look.haze.density, look.haze.base, look.haze.falloff);
  u.uLpHaze2.value.set(look.haze.height, look.haze.max);
  u.uLpLamp.value = look.lamps ?? 0;
  u.uLpWet.value = look.wet ?? 0;
  const c = look.cloudShadow;
  setCloudShadow(u, c ? { strength: c.strength, coverage: c.coverage, scale: c.scale, softness: c.softness ?? CLOUD_SOFTNESS, offset: c.offset ?? [0, 0] } : NO_CLOUD_SHADOW);
  const g = look.grade;
  setGrade(u, g ? { saturation: g.saturation ?? 1, tint: g.tint ? hexSrgb(g.tint) : [1, 1, 1] } : NO_GRADE);
}

/** `#rrggbb` as sRGB 0 to 1 (no colour-space conversion). */
function hexSrgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** The haze factor the surface shader computes, for tests and for CPU-side fades: distance d (m), height y (m). */
export function hazeAt(h: LowPolySceneLook["haze"], d: number, y: number): number {
  const fd = 1 - Math.exp(-Math.max(0, d - h.near) * h.density);
  const fh = Math.exp(-Math.max(0, y - h.base) * h.falloff);
  return Math.min(h.max, Math.max(0, fd * (1 - h.height + h.height * fh)));
}

const HAZE_GLSL = /* glsl */ `
  {
    float lpD = length(vLpWorld - cameraPosition);
    float lpFd = 1.0 - exp(-max(0.0, lpD - uLpHaze.x) * uLpHaze.y);
    float lpFh = exp(-max(0.0, vLpWorld.y - uLpHaze.z) * uLpHaze.w);
    float lpF = clamp(lpFd * mix(1.0, lpFh, uLpHaze2.x), 0.0, uLpHaze2.y);
    outgoingLight = mix(outgoingLight, uLpHazeColor, lpF * (1.0 - 0.6 * lpG));
  }
`;

/** The shader text the surface patch adds (exported for tests). */
export const LOWPOLY_CHUNKS = {
  vertDecl: /* glsl */ `attribute float ${LOWPOLY_GLOW_ATTRIBUTE};\nvarying float vLpGlow;\nvarying vec3 vLpWorld;\n`,
  vertBody: /* glsl */ `#include <project_vertex>
  {
    vec4 lpW = vec4(transformed, 1.0);
    #ifdef USE_INSTANCING
      lpW = instanceMatrix * lpW;
    #endif
    vLpWorld = (modelMatrix * lpW).xyz;
    vLpGlow = ${LOWPOLY_GLOW_ATTRIBUTE};
  }`,
  fragDecl: /* glsl */ `uniform float uLpGlow;\nuniform vec3 uLpHazeColor;\nuniform vec4 uLpHaze;\nuniform vec2 uLpHaze2;\nuniform float uLpLamp;\nuniform float uLpWet;\nvarying float vLpGlow;\nvarying vec3 vLpWorld;\n${CLOUD_GLSL.decl}${GRADE_GLSL.decl}`,
  /** After three's color_fragment: the colour grade (skipped while it is the identity). */
  gradeBody: GRADE_GLSL.body,
  fragBody: /* glsl */ `float lpG = vLpGlow > 1.5 ? uLpLamp : vLpGlow;
  ${CLOUD_GLSL.body}
  {
    // wet ground: faces turned up darken (the flat normal is in view space; world up in view space from viewMatrix)
    float lpUp = clamp(dot(normal, normalize((viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz)), 0.0, 1.0);
    outgoingLight *= 1.0 - uLpWet * lpUp * (1.0 - lpG);
  }
  outgoingLight = mix(outgoingLight, diffuseColor.rgb * uLpGlow, lpG);\n${HAZE_GLSL}\n#include <opaque_fragment>`,
};

/** Patch a shader object (as three passes to onBeforeCompile) with the low-poly surface: glow, haze, cloud shadow
 *  (cloud-shadow.ts; skipped while its strength is 0) and the colour grade (grade.ts; skipped while the identity), and with `sway` the foliage push (geometry with a `sway` weight
 *  bends away from the pushers; foliage.ts). The fragment shader must be one of three's lit ones (it reads
 *  `reflectedLight`). */
export function patchLowPolyShader(shader: { vertexShader: string; fragmentShader: string; uniforms: Record<string, THREE.IUniform> }, u: LowPolyUniforms, o: { sway?: boolean } = {}): void {
  Object.assign(shader.uniforms, u);
  const need = (src: string, chunk: string, where: string): void => {
    if (!src.includes(chunk)) throw new Error(`@rpg/look: low-poly patch: ${where} has no ${chunk}`);
  };
  need(shader.vertexShader, "#include <project_vertex>", "vertex shader");
  need(shader.fragmentShader, "#include <opaque_fragment>", "fragment shader");
  // the cloud shadow takes its share of the direct light (three's lit materials sum it into outgoingLight)
  need(shader.fragmentShader, "reflectedLight.directDiffuse", "fragment shader");
  // the grade works on the surface's own colour, after the vertex colours are in
  need(shader.fragmentShader, "#include <color_fragment>", "fragment shader");
  shader.vertexShader = LOWPOLY_CHUNKS.vertDecl + shader.vertexShader.replace("#include <project_vertex>", LOWPOLY_CHUNKS.vertBody);
  shader.fragmentShader = LOWPOLY_CHUNKS.fragDecl + shader.fragmentShader.replace("#include <color_fragment>", LOWPOLY_CHUNKS.gradeBody).replace("#include <opaque_fragment>", LOWPOLY_CHUNKS.fragBody);
  // the push goes in before the surface's own project_vertex block, so its world position sees the bent plant
  if (o.sway) patchSwayShader(shader, u);
}

/**
 * The one surface material: Lambert, flat-shaded (face normals from screen derivatives, so both sides light),
 * vertex-coloured, double-sided, with glow, haze and the foliage push. Every geometry on it needs a `glow` attribute
 * (`ensureGlow`) and should carry a `sway` one (`ensureSway`; the look's `track` gives both; without one the
 * material's default of 0 applies). Share it: one program for the whole scene.
 */
export function lowPolySurface(u: LowPolyUniforms): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, side: THREE.DoubleSide });
  m.name = "lowpoly-surface";
  m.onBeforeCompile = (s) => patchLowPolyShader(s, u, { sway: true });
  m.customProgramCacheKey = () => "lowpoly-surface-3" + swayKey();
  defaultSway(m);
  return m;
}

/** Give a geometry a zero `glow` attribute if it has none (terrain, props built without meshkit's facet). */
export function ensureGlow(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!geo.getAttribute(LOWPOLY_GLOW_ATTRIBUTE)) geo.setAttribute(LOWPOLY_GLOW_ATTRIBUTE, new THREE.BufferAttribute(new Float32Array(geo.getAttribute("position").count), 1));
  return geo;
}

/** The sky dome: a gradient from `ground` below the horizon through `horizon` to `top`, with a soft sun glow. */
export function lowPolySky(look: LowPolySceneLook, radius: number): THREE.Mesh {
  const sunDir = sunDirection(look.sun);
  const mat = new THREE.ShaderMaterial({
    name: "lowpoly-sky",
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      top: { value: new THREE.Color(look.sky.top) },
      horizon: { value: new THREE.Color(look.sky.horizon) },
      below: { value: new THREE.Color(look.sky.ground) },
      sunDir: { value: sunDir },
      sunColor: { value: new THREE.Color(look.sun.colour) },
    },
    vertexShader: /* glsl */ `varying vec3 vDir; void main() { vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_Position.z = gl_Position.w; }`,
    fragmentShader: /* glsl */ `
      uniform vec3 top; uniform vec3 horizon; uniform vec3 below; uniform vec3 sunDir; uniform vec3 sunColor;
      varying vec3 vDir;
      void main() {
        vec3 d = normalize(vDir);
        vec3 c = d.y >= 0.0 ? mix(horizon, top, smoothstep(0.0, 0.35, d.y)) : mix(horizon, below, smoothstep(0.0, -0.25, d.y));
        c += sunColor * pow(max(dot(d, sunDir), 0.0), 48.0) * 0.6;
        gl_FragColor = vec4(c, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
  const sky = new THREE.Mesh(new THREE.SphereGeometry(radius, 24, 12), mat);
  sky.name = "lowpoly-sky";
  sky.frustumCulled = false;
  sky.renderOrder = -1000;
  return sky;
}

/** The unit vector toward the sun. */
export function sunDirection(sun: LowPolySceneLook["sun"]): THREE.Vector3 {
  const c = Math.cos(sun.elevation);
  return new THREE.Vector3(Math.sin(sun.azimuth) * c, Math.sin(sun.elevation), Math.cos(sun.azimuth) * c);
}

/** What the look reports. */
export interface LowPolyReport {
  tier: LowPolyTier;
  postPasses: number;
  shadowMapSize: number;
  blobs: number;
  pixelRatio: number;
}

/** The low-poly look on a scene. */
export interface LowPolyLook {
  /** The one surface material (vertex colours, glow, haze, the foliage push). */
  surface: THREE.MeshLambertMaterial;
  /** The surface's depth twin for the shadow pass (the same push): `track` sets it on meshes whose geometry sways. */
  surfaceDepth: THREE.MeshDepthMaterial;
  /** The surface's shared uniforms (haze, glow, lamps, wet, cloud shadow, the push): a time-of-day driver writes them
   *  every frame, the game's pushers go in with `setPushers` (foliage.ts), and other materials that patch in the
   *  low-poly surface (the kit's, the water's) share them, so they follow `setLook`. */
  uniforms: LowPolyUniforms;
  /** The live unit vector toward the sun light (the shadow box and the dome follow it; a driver may write it). */
  sunDirection: THREE.Vector3;
  palette(c: string): THREE.Color;
  sun: THREE.DirectionalLight;
  fill: THREE.HemisphereLight;
  sky: THREE.Mesh;
  /** The post chain, or null (the scene draws straight to the canvas). */
  composer: EffectComposer | null;
  /** Prepare a root added to the scene: casters per the tier, `glow` and `sway` on its surface geometries (the depth
   *  twin on those that sway), a blob under a character. */
  track(root: THREE.Object3D, o?: { blob?: number }): void;
  /** Stop tracking a root (its blob goes). */
  untrack(root: THREE.Object3D): void;
  /** The point the sun's shadow and the sky follow (the player, or the camera's target). */
  focus(p: THREE.Vector3): void;
  /** Draw a frame (renderer.info counts the whole frame, post passes included). */
  render(): void;
  /** Compile every program the scene needs and draw once (call at boot, before the compile watchdog arms). */
  warm(): void;
  /** Change the scene look (palette colours are read at build; light, sky and haze change at once). */
  setLook(look: LowPolySceneLook): void;
  resize(w: number, h: number): void;
  report(): LowPolyReport;
}

/**
 * Put the low-poly look on a scene: lights, sky, the surface material, the tier's shadow map, blobs and post chain.
 * `shadowExtent` is the half-size (m) of the sun's shadow box around the focus.
 */
export function createLowPolyLook(o: {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  look: LowPolySceneLook;
  tier: LowPolyTier;
  shadowExtent?: number;
  maxBlobs?: number;
  /** A sky dome in place of the gradient one (`createSkyDome`: sun, moon, stars, clouds). It keeps the gradient's
   *  uniform names (top, horizon, below, sunDir, sunColor), so `setLook` still writes it. */
  sky?: THREE.Mesh;
  /** Extra post passes, run between the scene and bloom, on tiers with a post chain (`depth`: the scene's target
   *  carries a depth texture, for passes that read it, e.g. `ShaftPass`). Ignored on tiers without post. */
  post?: { depth?: boolean; passes?: readonly Pass[] };
}): LowPolyLook {
  const { renderer, scene, camera, tier } = o;
  let look = o.look;
  const extent = o.shadowExtent ?? 14;
  const u = lowPolyUniforms(look);
  let palette = createPalette(look.palette);

  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = look.exposure;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setPixelRatio(Math.min(typeof devicePixelRatio === "number" ? devicePixelRatio : 1, tier.pixelRatioCap));
  renderer.shadowMap.enabled = tier.shadowMapSize > 0;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.info.autoReset = false;
  scene.background = null;
  scene.fog = null;

  const surface = lowPolySurface(u);
  const surfaceDepth = swayDepthMaterial(u);
  const sun = new THREE.DirectionalLight(look.sun.colour, look.sun.intensity);
  sun.name = "lowpoly-sun";
  sun.castShadow = tier.shadowMapSize > 0;
  sun.shadow.mapSize.set(tier.shadowMapSize || 1, tier.shadowMapSize || 1);
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.04;
  Object.assign(sun.shadow.camera, { left: -extent, right: extent, top: extent, bottom: -extent, near: 0.5, far: extent * 4 });
  sun.shadow.camera.updateProjectionMatrix();
  const fill = new THREE.HemisphereLight(look.fill.sky, look.fill.ground, look.fill.intensity);
  fill.name = "lowpoly-fill";
  const sky = o.sky ?? lowPolySky(look, camera.far * 0.9);
  scene.add(sun, sun.target, fill, sky);

  // blobs: one instanced mesh for every tracked character
  const maxBlobs = o.maxBlobs ?? 64;
  const blobMat = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.22, depthWrite: false, fog: false, polygonOffset: true, polygonOffsetFactor: -2 });
  blobMat.name = "lowpoly-blob";
  const blobGeo = new THREE.CircleGeometry(1, 10).rotateX(-Math.PI / 2);
  const blobs = new THREE.InstancedMesh(blobGeo, blobMat, maxBlobs);
  blobs.name = "lowpoly-blobs";
  blobs.frustumCulled = false;
  blobs.count = 0;
  blobs.visible = tier.blobShadows;
  if (tier.blobShadows) scene.add(blobs);
  const blobbed = new Map<THREE.Object3D, number>();

  // post: render, bloom, output on the tiers with bloom; else none
  let composer: EffectComposer | null = null;
  if (tier.bloom) {
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    const target = new THREE.WebGLRenderTarget(size.x, size.y, { type: THREE.HalfFloatType, samples: tier.msaa ? 4 : 0 });
    if (o.post?.depth) target.depthTexture = new THREE.DepthTexture(size.x, size.y);
    composer = new EffectComposer(renderer, target);
    composer.addPass(new RenderPass(scene, camera));
    for (const p of o.post?.passes ?? []) composer.addPass(p);
    composer.addPass(new UnrealBloomPass(new THREE.Vector2(size.x, size.y), tier.bloom.strength, tier.bloom.radius, tier.bloom.threshold));
    composer.addPass(new OutputPass());
  }

  const focusAt = new THREE.Vector3();
  const sunDir = sunDirection(look.sun);
  const placeSun = (): void => {
    // the shadow box follows the focus, snapped to whole shadow texels so its edges don't swim
    const texel = (2 * extent) / Math.max(1, tier.shadowMapSize);
    const snap = (v: number) => Math.round(v / texel) * texel;
    const c = new THREE.Vector3(snap(focusAt.x), snap(focusAt.y), snap(focusAt.z));
    sun.target.position.copy(c);
    sun.position.copy(c).addScaledVector(sunDir, extent * 2);
    sun.target.updateMatrixWorld();
  };
  placeSun();

  const m4 = new THREE.Matrix4();
  const wp = new THREE.Vector3();
  const updateBlobs = (): void => {
    if (!tier.blobShadows) return;
    let i = 0;
    for (const [root, r] of blobbed) {
      if (i >= maxBlobs) break;
      root.getWorldPosition(wp);
      m4.makeScale(r, 1, r).setPosition(wp.x, wp.y + 0.015, wp.z);
      blobs.setMatrixAt(i++, m4);
    }
    blobs.count = i;
    blobs.instanceMatrix.needsUpdate = true;
  };

  const self: LowPolyLook = {
    surface,
    surfaceDepth,
    uniforms: u,
    sunDirection: sunDir,
    palette: (c) => palette(c),
    sun,
    fill,
    sky,
    composer,
    track(root, t = {}) {
      const character = !!root.userData.character;
      root.traverse((x) => {
        const m = x as THREE.Mesh;
        if (!m.isMesh) return;
        if (m.material === surface) {
          ensureGlow(m.geometry);
          // a swaying mesh casts its bent shadow; every other keeps three's own depth material
          if (sways(m.geometry)) m.customDepthMaterial = surfaceDepth;
          ensureSway(m.geometry);
        }
        m.castShadow = tier.shadowMapSize > 0 && (tier.shadowCasters === "all" || character) && m.castShadow !== false;
        m.receiveShadow = tier.shadowMapSize > 0;
      });
      if (character && t.blob) blobbed.set(root, t.blob);
    },
    untrack(root) {
      blobbed.delete(root);
    },
    focus(p) {
      focusAt.copy(p);
    },
    render() {
      renderer.info.reset();
      sky.position.copy(camera.position);
      placeSun();
      updateBlobs();
      if (composer) composer.render();
      else renderer.render(scene, camera);
    },
    warm() {
      updateBlobs();
      renderer.compile(scene, camera);
      self.render();
    },
    setLook(l) {
      look = l;
      palette = createPalette(l.palette);
      setLowPolyUniforms(u, l);
      renderer.toneMappingExposure = l.exposure;
      sun.color.set(l.sun.colour);
      sun.intensity = l.sun.intensity;
      sunDir.copy(sunDirection(l.sun));
      fill.color.set(l.fill.sky);
      fill.groundColor.set(l.fill.ground);
      fill.intensity = l.fill.intensity;
      const su = (sky.material as THREE.ShaderMaterial).uniforms;
      su.top!.value.set(l.sky.top);
      su.horizon!.value.set(l.sky.horizon);
      su.below!.value.set(l.sky.ground);
      su.sunDir!.value.copy(sunDir);
      su.sunColor!.value.set(l.sun.colour);
    },
    resize(w, h) {
      renderer.setSize(w, h);
      composer?.setSize(w, h);
    },
    report: () => ({
      tier,
      postPasses: composer ? composer.passes.filter((p) => p.enabled && !(p instanceof RenderPass)).length : 0,
      shadowMapSize: sun.castShadow ? sun.shadow.mapSize.x : 0,
      blobs: tier.blobShadows ? blobbed.size : 0,
      pixelRatio: renderer.getPixelRatio(),
    }),
  };
  return self;
}
